import styled, { createGlobalStyle, css } from 'styled-components';
import { changeColorAlpha, Show } from '@elfsight/lib-utils';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  Loader
} from '@elfsight/widget-ui';
import {
  getBrightInvertedColor,
  useWidgetRectRef
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../meta';
import { Deactivation } from './components/Deactivation';
import { GalleryContainer } from './components/GalleryContainer';
import { useImages } from './components/images';
import { ImageLazyLoadingProvider } from './components/lazy-loading';
import { useLightbox } from './components/lightbox';
import { getSecondColor } from './helpers';

export function Widget() {
  const settings = useSettings();
  const {
    editMode,
    backgroundType,
    backgroundImage,
    backgroundColor,
    backgroundImageOverlayColor,
    popupOverlayColor,
    popupTextColor,
    heading,
    popupElements,
    headingTextColor,
    headingFontSize,
    width,
    font,
    customCSS,
    customJS,
    renderThumbnailMode,
    widgetId
  } = settings;

  const widgetRectRef = useWidgetRectRef();

  const { isLoading, images, hasGoogleIntegration } = useImages();
  const { lightboxContainerClass } = useLightbox();

  const freeLinkSettings = pickFreeLinkSettings(settings);

  const secondColor = getSecondColor({
    backgroundType,
    backgroundColor,
    backgroundImageOverlayColor
  });

  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    meta.appAlias,
    widgetId
  );

  const shouldShowStatus =
    isLoading || (editMode && hasGoogleIntegration && !images.length);

  const shouldShowGallery =
    (!isLoading && hasGoogleIntegration && !!images.length) ||
    !hasGoogleIntegration;

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);

  return (
    <RootComponent
      id={`eapps-${meta.appAlias}-${widgetId}`}
      ref={widgetRectRef}
      className={customCSSPrefixClassName}
    >
      <Deactivation>
        <StyledEmbedRoot
          width={width}
          backgroundType={backgroundType}
          backgroundColor={backgroundColor}
          backgroundImage={backgroundImage}
          backgroundImageOverlayColor={backgroundImageOverlayColor}
          headingFontSize={headingFontSize}
          headingTextColor={headingTextColor}
          font={font?.value}
        >
          <CustomCSS
            customCSS={customCSS}
            prefixClassName={customCSSPrefixClassName}
          />
          <GlobalPopupStyles
            lightboxContainerClass={lightboxContainerClass}
            popupElements={popupElements}
            popupOverlayColor={popupOverlayColor}
            popupTextColor={popupTextColor}
            font={font?.value}
          />

          <Show when={!!heading}>
            <HeadingContainer>{heading}</HeadingContainer>
          </Show>

          <Show when={shouldShowStatus}>
            <StatusWrapper>
              <StatusContainer>
                <Show
                  when={!isLoading}
                  fallback={<Loader color={secondColor} size={40} />}
                >
                  <StatusText _color={secondColor}>
                    Your Google Photos has no photos yet. Add photos to your
                    album to see them here!
                  </StatusText>
                </Show>
              </StatusContainer>
            </StatusWrapper>
          </Show>

          <Show when={shouldShowGallery}>
            <ImageLazyLoadingProvider>
              <GalleryContainer />
            </ImageLazyLoadingProvider>
          </Show>

          <FreeLink
            {...freeLinkSettings}
            appAlias={meta.appAlias}
            appName={meta.appName}
            style={{
              link: {
                marginTop: '20px'
              }
            }}
          />
        </StyledEmbedRoot>
      </Deactivation>
    </RootComponent>
  );
}

const RootComponent = styled.div.attrs({
  className: 'RootLayout__RootComponent-sc' // legacy class
})`
  width: 100%;
`;

const GlobalPopupStyles = createGlobalStyle`
  ${({
    lightboxContainerClass,
    popupOverlayColor,
    popupTextColor,
    popupElements,
    font
  }) => css`
    .${lightboxContainerClass}.fslightbox-container {
      ${popupOverlayColor && `background: ${popupOverlayColor};`}
      ${font
        ? `font-family: "${font}";`
        : `
            font-weight: 400;
            font-style: normal;
            font-size: 15px;
            line-height: 1.3;
        `}
    }

    .${lightboxContainerClass} .fslightbox-toolbar {
      ${popupOverlayColor &&
      `background: ${changeColorAlpha(popupOverlayColor, -0.35)};`}
    }

    .${lightboxContainerClass} .fslightbox-slide-btn {
      ${popupOverlayColor &&
      `background: ${changeColorAlpha(popupOverlayColor, -0.35)};`}
    }

    .${lightboxContainerClass} .fslightbox-svg-path {
      ${popupOverlayColor &&
      `fill: ${getBrightInvertedColor(
        changeColorAlpha(popupOverlayColor, -0.35),
        ['rgb(255, 255, 255)', 'rgb(29, 33, 41)']
      )};`}
    }

    .${lightboxContainerClass} .fslightbox-slide-number-container {
      ${popupOverlayColor &&
      `color: ${getBrightInvertedColor(popupOverlayColor, [
        'rgb(255, 255, 255)',
        'rgb(29, 33, 41)'
      ])};`}
    }

    .${lightboxContainerClass} .fslightbox-caption-wrapper {
      bottom: 130px;
    }

    .${lightboxContainerClass} .fslightbox-caption-title,
    .${lightboxContainerClass} .fslightbox-caption-description,
    .${lightboxContainerClass} .fslightbox-caption-categories-category,
    .${lightboxContainerClass} .fslightbox-caption-categories-separator {
      color: ${popupTextColor};
    }

    .${lightboxContainerClass} .fslightbox-caption-categories-category {
      opacity: 0.7;
    }

    .${lightboxContainerClass} .fslightbox-caption-title {
      ${popupElements.includes('title') ? '' : 'display: none;'}
    }

    .${lightboxContainerClass} .fslightbox-caption-description {
      ${popupElements.includes('description') ? '' : 'display: none;'}
    }

    .${lightboxContainerClass} .fslightbox-caption-categories {
      ${popupElements.includes('categories') ? '' : 'display: none;'}
    }
  `}
`;

const HeadingContainer = styled.div.attrs({
  className: `${meta.stylePrefix}-photo-gallery-heading`
})``;

const StyledEmbedRoot = styled(EmbedRoot).attrs({
  className: `${meta.stylePrefix}-photo-gallery-component`
})`
  width: 100%;
  direction: ltr;
  font-weight: 400;
  font-style: normal;
  font-size: 15px;
  line-height: 1.3;

  ${({
    backgroundType,
    backgroundImage,
    backgroundColor,
    backgroundImageOverlayColor,
    headingTextColor,
    headingFontSize,
    font
  }) => css`
    ${(backgroundType === 'image' || backgroundType === 'color') &&
    'padding: 40px;'}

    ${backgroundImage &&
    backgroundType === 'image' &&
    `background-image: url(${
      backgroundImage.data?.url || backgroundImage.url || ''
    });`}

    ${backgroundColor &&
    backgroundType === 'color' &&
    `background-color: ${backgroundColor};`}

    &::before {
      ${backgroundImageOverlayColor &&
      backgroundType === 'image' &&
      css`
        background-color: ${backgroundImageOverlayColor};
        content: ' ';
        inset: 0;
        position: absolute;
      `}
    }

    ${font && `font-family: "${font}";`}

    & ${HeadingContainer} {
      line-height: 40px;
      text-align: center;
      margin-bottom: 40px;
      position: relative;

      color: ${headingTextColor || '#111111'};
      font-size: ${headingFontSize || 24}px;
    }
  `}
`;

const StatusWrapper = styled.div`
  width: 100%;
  position: relative;
  height: 0;
  padding-top: 56%;
`;

const StatusContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
`;

const StatusText = styled.div`
  text-align: center;

  ${({ _color }) =>
    _color &&
    `
      color: ${_color};
    `}
`;
